/**
 * The shapes the brand page's sections are drawn from.
 *
 * Same contract as `@codezo/quote-detail`: nothing here is a GraphQL type. The
 * apps map `getBrand` onto these, which is what lets the admin and the org page
 * render the same sections from two different queries — and what keeps a
 * backend field rename out of the layout.
 *
 * Dates and file sizes arrive RAW (ISO string, bytes) rather than formatted,
 * unlike the quote package's money. A brand carries no currency and no
 * locale-sensitive figures, so there is nothing an app knows about formatting
 * these that the package doesn't — and a caller passing `updatedLabel` strings
 * would have to reimplement the same two lines twice.
 */

export type Tone = 'neutral' | 'info' | 'success' | 'warn' | 'danger';

/* ────────────────────────── colors ────────────────────────── */

import type { BrandPantone } from './pantone';

/** The four buckets `BrandColorPalette` is grouped into, in display order. */
export type BrandColorCategory = 'primary' | 'secondary' | 'accent' | 'neutral';

export const BRAND_COLOR_CATEGORIES: BrandColorCategory[] = [
	'primary',
	'secondary',
	'accent',
	'neutral',
];

export interface BrandSwatch {
	name: string;
	/** The SCREEN colour, and the source of truth for anything painted. */
	hex: string;
	/** What the colour is for, in the studio's own words. Free text, not tags —
	 * it was a `string[]` that was only ever typed into one box and joined back
	 * together to display. */
	usage?: string | null;
	/**
	 * What this colour is PRINTED as. Independent of `hex` — never paint from
	 * it, and don't treat the gap between the two as a discrepancy: one is an
	 * ink, the other is a screen.
	 */
	pantone?: BrandPantone | null;
	category: BrandColorCategory;
	/** Position within its own category — what a delete has to address, since
	 * the palette is replaced whole and swatches carry no id. */
	index: number;
}

/* ────────────────────────── logo ────────────────────────── */

/**
 * The background a logo tile is previewed against.
 *
 * `auto` is the checkerboard — it shows transparency, which is the one thing
 * the other three hide. The rest answer "does this file survive on a light
 * page / a dark page / our own brand color", which is the question the old flat
 * grid left a designer to answer by downloading the file.
 */
export type BrandLogoContext = 'auto' | 'light' | 'dark' | 'brand';

export interface BrandLogoVariant {
	id: string;
	name: string;
	url?: string | null;
	thumbnailUrl?: string | null;
	mimeType: string;
	size: number;
	/** `BrandFile.designVariant` — "Primary mark", "Wordmark", "Monochrome". */
	variantLabel?: string | null;
	/**
	 * `BrandFile.backgroundType` — the contexts this file is FOR. A tile whose
	 * backgrounds don't include the active context is dimmed rather than
	 * hidden: "we have no dark-background mark" is information, and hiding the
	 * tile would make the grid silently shorter instead.
	 */
	backgrounds?: string[] | null;
	/** The brand's `logo` field, if this file is it. */
	isPrimary?: boolean;
}

/* ────────────────────────── typography ────────────────────────── */

export interface BrandTypeface {
	/** "Primary" / "Secondary" — the role, not the family. */
	role: string;
	family: string;
	/** Weights and styles gathered from the brand's font files. */
	styles?: string[];
	/** True when font files back this family, false when it is only a name. */
	hasFiles?: boolean;
}

export interface BrandFontFile {
	id: string;
	family: string;
	name: string;
	style?: string | null;
	weight?: string | null;
	url?: string | null;
}

/* ────────────────────────── voice ────────────────────────── */

export interface BrandVoice {
	tone?: string | null;
	traits?: string[] | null;
	values?: string[] | null;
}

/* ────────────────────────── assets ────────────────────────── */

export interface BrandAsset {
	id: string;
	name: string;
	url?: string | null;
	thumbnailUrl?: string | null;
	mimeType: string;
	size: number;
	uploadedAt?: string | null;
	/** `BrandFileType` — drives the category filter strip. */
	category: string;
	tags?: string[] | null;
}

/* ────────────────────────── page frame ────────────────────────── */

export interface BrandStat {
	label: string;
	value: string | number;
	/** The section this figure counts. Given one, the cell becomes the way to
	 * jump there — the count and the link to what it counts are the same
	 * affordance, so there is no second control to find. */
	sectionId?: string;
}

/**
 * A section, declared once by the page and arranged by `BrandSections`.
 *
 * `count` rides in the rail beside the label. It is optional because not every
 * section has a meaningful one — "Who we are" is one story, not N of anything.
 */
export interface BrandLayoutSection {
	id: string;
	label: string;
	count?: number;
	content: React.ReactNode;
	/** Skipped entirely — rail entry and all. An empty section a user can't
	 * fill (no permission, no data, nothing to add) is noise in the index. */
	hidden?: boolean;
}
